import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useSymphonyApiService } from '../../../hooks/useSymphonyApi';
import { ToastContext } from 'cai-fusion';
import { debugLog } from '../../../configs/loggingConfig';

const ModelsContext = createContext();

function ModelsProvider({ children }) {

    const { createToast } = useContext(ToastContext);
    const apiServiceClient = useSymphonyApiService("maestro", "v3");

    const [models, setModels] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [currModel, setCurrModel] = useState({});
    const [currModelParams, setCurrModelParams] = useState([]);
    const [isEditing, setIsEditing] = useState(false);

    // Initialize with all necessary fields for creating a new model
    const placeholderModel = {
		engine: "",
		deploymentName: "",
		modelName: "",
		displayName: "",
		description: "",
		tokenLimit: 0,
		outputTokenLimit: 0,
		modelApiEndpoint: "",
		modelApiKey: "",
		modelApiVersion: "",
		modelSource: "",
		deploymentLocation: "",
		inputCostPerUnit: 0.0,
		outputCostPerUnit: 0.0,
		isEnabled: false,
		index: 0,
		isTextModel: false,
		isImageModel: false,
		isVisionModel: false,
		isDefaultTextModel: false,
		isDefaultImageModel: false,
		isDefaultVisionModel: false,
		encodingEngine: "cl100k_base",
	};
    const placeholderParams = [
        {
            "name": "Temperature",
            "description": "The 'temperature' setting changes how wild or tame the responses are. If you set it to a higher number, like 1, you'll get more surprising and creative answers. If you set it to a lower number, like 0, the answers will be more predictable and focused. But be careful with changing this setting and the 'Top P' setting at the same time - it's best to adjust one or the other, not both.",
            "modelParameterId": 1,
            "modelParameter": {
                "id": 1,
                "name": "Temperature",
                "description": "The 'temperature' setting changes how wild or tame the responses are. If you set it to a higher number, like 1, you'll get more surprising and creative answers. If you set it to a lower number, like 0, the answers will be more predictable and focused. But be careful with changing this setting and the 'Top P' setting at the same time - it's best to adjust one or the other, not both."
            },
            "modelId": "gpt-3.5-0613-base",
            "model": null,
            "defaultValue": 0.7,
            "minValue": 0,
            "maxValue": 1,
            "dataType": "double"
        },
        {
            "name": "Top-p",
            "description": "The 'Top P' setting is another way to control how unusual or typical the responses are. Think of it as a filter for the chat's word choices. If you set it to a lower number like 0.5, the chat will mostly stick to common and expected words and phrases, giving you straightforward and predictable answers. But if you turn it up to a higher number like 0.9, the chat has the freedom to pick from a wider range of words, including some that might be less common or more surprising. This allows for more creative and unexpected responses. Just like with the temperature setting, you can use Top P to make the chat more imaginative or more focused, but remember, it's usually best to adjust either the temperature or Top P, not both at the same time.",
            "modelParameterId": 2,
            "modelParameter": {
                "id": 2,
                "name": "Top-p",
                "description": "The 'Top P' setting is another way to control how unusual or typical the responses are. Think of it as a filter for the chat's word choices. If you set it to a lower number like 0.5, the chat will mostly stick to common and expected words and phrases, giving you straightforward and predictable answers. But if you turn it up to a higher number like 0.9, the chat has the freedom to pick from a wider range of words, including some that might be less common or more surprising. This allows for more creative and unexpected responses. Just like with the temperature setting, you can use Top P to make the chat more imaginative or more focused, but remember, it's usually best to adjust either the temperature or Top P, not both at the same time."
            },
            "modelId": "gpt-3.5-0613-base",
            "model": null,
            "defaultValue": 0.95,
            "minValue": 0,
            "maxValue": 1,
            "dataType": "double"
        },
        {
            "name": "Frequency Penalty",
            "description": "The 'Frequency Penalty' setting helps to make the conversation more varied and interesting. By turning this setting up, you tell the chat not to repeat the same words or phrases too often. If a word has been used a lot already, the chat will be less likely to use it again. It's like encouraging the chat to use different words and expressions, so the conversation doesn't get repetitive.",
            "modelParameterId": 3,
            "modelParameter": {
                "id": 3,
                "name": "Frequency Penalty",
                "description": "The 'Frequency Penalty' setting helps to make the conversation more varied and interesting. By turning this setting up, you tell the chat not to repeat the same words or phrases too often. If a word has been used a lot already, the chat will be less likely to use it again. It's like encouraging the chat to use different words and expressions, so the conversation doesn't get repetitive."
            },
            "modelId": "gpt-3.5-0613-base",
            "model": null,
            "defaultValue": 0,
            "minValue": 0,
            "maxValue": 2,
            "dataType": "double"
        },
        {
            "name": "Presence Penalty",
            "description": "The 'Presence Penalty' setting is like a guide that encourages the conversation to keep moving to new topics. By setting this number higher, you tell the chat to avoid repeating words or ideas that have already been used. This nudges the conversation to explore new subjects and ideas instead of sticking to the same ones. It's like having a friend who always wants to keep the conversation fresh and exciting by talking about different things.",
            "modelParameterId": 4,
            "modelParameter": {
                "id": 4,
                "name": "Presence Penalty",
                "description": "The 'Presence Penalty' setting is like a guide that encourages the conversation to keep moving to new topics. By setting this number higher, you tell the chat to avoid repeating words or ideas that have already been used. This nudges the conversation to explore new subjects and ideas instead of sticking to the same ones. It's like having a friend who always wants to keep the conversation fresh and exciting by talking about different things."
            },
            "modelId": "gpt-3.5-0613-base",
            "model": null,
            "defaultValue": 0,
            "minValue": 0,
            "maxValue": 2,
            "dataType": "double"
        },
        {
            "name": "Max Tokens",
            "description": "The 'max tokens' setting lets you control how long or short the answers are. If you want shorter and more concise answers, set this number lower. If you prefer longer and more detailed responses, just turn the number up.",
            "modelParameterId": 5,
            "modelParameter": {
                "id": 5,
                "name": "Max Tokens",
                "description": "The 'max tokens' setting lets you control how long or short the answers are. If you want shorter and more concise answers, set this number lower. If you prefer longer and more detailed responses, just turn the number up."
            },
            "modelId": "gpt-3.5-0613-base",
            "model": null,
            "defaultValue": 800,
            "minValue": 0,
            "maxValue": null,
            "dataType": "int"
        }
    ];

    // When opening up the edit modal, we set the form's object to a template if it's a new object, or the current model's object.
    const initializeModifyModel = async (engine = null) => {
        if (engine === null) {
            setCurrModel({...placeholderModel})
            setCurrModelParams([...placeholderParams]);
            setIsEditing(false);
            return;
        }

        setCurrModel(models.find((model) => model.engine === engine));
        setIsEditing(true);
        fetchModelParameters(engine);
    }

    // For a given model, fetch the parameters from the API. This will be loaded AFTER the modal opens.
    const fetchModelParameters = async (engine) => {
        setIsLoading(true);
        try {
            const modelParams = await apiServiceClient.Models.getModelParameters(engine);
            setCurrModelParams(modelParams);
        } catch (error) {
            handleApiError("Error fetching model parameters.", error);
        } finally {
            setIsLoading(false);
        }
    }

    const resetCurrModel = () => {
        setCurrModel({});
        setCurrModelParams([]);
    }

    // Load all models. True on admin means all model information gotten with admin verification.
    const loadModels = async (admin = false) => {
        setIsLoading(true);
        try {
            const respModels = await apiServiceClient.Models.getModels();
            setModels(respModels); 
        } catch (error) {
            handleApiError("Error fetching models.", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Create a model in the API with the given fields and parameters.
    // There is no call to initialize parameters when creating, so they must be updated.
    const createModel = async (model, modelParams) => {
        try {
            const newModel = await apiServiceClient.Models.createModel(model);
            setModels([...models, newModel]);
            createToast("Model created successfully.", "success");
            await updateModelParameters(newModel?.engine, modelParams);
        } catch (error) {
            handleApiError("Error creating model.", error);
        }
    };

    // Updates a model in the API with the given fields and parameters.
    // Uses a secondary call to update the parameters.
    const updateModel = async (engine, updatedModel, modelParams) => {
        try {
            const respModelObj = await apiServiceClient.Models.updateModel(engine, updatedModel);
            setModels(models.map((model) => model.engine === engine ? respModelObj : model));
            createToast("Model updated successfully.", "success");
            await updateModelParameters(engine, modelParams);
        } catch (error) {
            handleApiError("Error updating model.", error);
        }
    };

    // Updates the model parameters and throws an error if they fail.
    const updateModelParameters = async (engine, parameters = []) => {
        try {
            await apiServiceClient.Models.updateModelParameters(engine, parameters);
        } catch (error) {
            handleApiError("Error handling model parameters.", error);
        }
    }

    // Deletes model and provides user feedback.
    const deleteModel = async (engine) => {
        try {
            await apiServiceClient.Models.deleteModel(engine);
            setModels(models.filter(m => m.engine !== engine));
            createToast("Model deleted successfully.", "success");
        } catch (error) {
            handleApiError("Error deleting model.", error);
        }
    };

    // Helper method for printing API errors.
    const handleApiError = (message, error) => {
        debugLog.error(message, error);
        createToast(message, "error");
        setIsLoading(false);
    }

    useEffect(() => {
        loadModels();
    }, []);

    return (
        <ModelsContext.Provider 
            value={{ 
                // States
                models, 
                isLoading, 
                currModel,
                currModelParams,
                isEditing,
                // Funcs
                initializeModifyModel,
                resetCurrModel,
                createModel, 
                updateModel,
                updateModelParameters,
                deleteModel,
                fetchModelParameters,
                loadModels, 
            }}
        >
            {children}
        </ModelsContext.Provider>
    );
}

function useModels() {
    const context = useContext(ModelsContext);
    if (!context) {
        throw new Error('useModels must be used within a ModelsProvider');
    }
    return context;
}

export { ModelsProvider, useModels };